import React, { useState } from 'react';
import { Crown, Code } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AdminPanel } from '@/components/AdminPanel';
import { VoteAPIManager } from '@/components/VoteAPIManager';
import { RankChangeBadge } from '@/components/RankChangeBadge';

interface ServerCardHeaderProps {
  rank: number;
  isSponsored: boolean;
  sponsorTier?: string;
  isGold: boolean;
  serverData?: {
    id: string;
    name: string;
    is_sponsored: boolean;
    prestige_expires_at: string | null;
    created_at: string;
    updated_at: string;
    owner_id: string;
    custom_color?: string;
  } | null;
  userId?: string;
  apiToken?: string;
  isCompetitiveMode?: boolean;
  competitiveVotes?: number;
  rankChange?: number;
}

export const ServerCardHeader: React.FC<ServerCardHeaderProps> = ({
  rank, isSponsored, sponsorTier, isGold, serverData, userId, apiToken,
  isCompetitiveMode = false, competitiveVotes, rankChange,
}) => {
  const [showAPIDialog, setShowAPIDialog] = useState(false);

  const getRankBadge = () => {
    if (isGold) return null;
    if (rank <= 3 && !isCompetitiveMode) {
      const rankConfig = {
        1: { class: 'rank-badge-gold', icon: '👑', text: '#1' },
        2: { class: 'rank-badge-silver', icon: '🥈', text: '#2' },
        3: { class: 'rank-badge-bronze animate-float-subtle', icon: '🥉', text: '#3' },
      };
      const config = rankConfig[rank as keyof typeof rankConfig];
      if (!config) return null;
      return (
        <div className={`rank-badge-premium-mini ${config.class}`}>
          <div className="rank-icon-mini">{config.icon}</div>
          <div className="rank-text-mini">{config.text}</div>
        </div>
      );
    }
    return (
      <div className={`rank-badge rank-badge-standard-mini ${isCompetitiveMode ? 'bg-gradient-to-r from-gaming-primary to-gaming-secondary' : ''}`}>
        <div className="rank-number-mini">#{rank}</div>
        {isCompetitiveMode && competitiveVotes !== undefined && (
          <div className="text-xs text-white/80 mt-0.5">{competitiveVotes} votes</div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Flux normal - pas d'absolute, juste une ligne flex */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {getRankBadge()}
        {rankChange !== undefined && <RankChangeBadge change={rankChange} />}
        {isSponsored && (
          <Badge
            className="animate-pulse font-bold text-xs px-2 py-0.5 shadow-lg"
            style={serverData?.custom_color ? {
              background: `linear-gradient(45deg, ${serverData.custom_color}, ${serverData.custom_color}dd)`,
              borderColor: serverData.custom_color,
              color: '#fff',
              textShadow: '0 1px 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.5)',
              boxShadow: `0 0 8px ${serverData.custom_color}80, 0 2px 4px rgba(0,0,0,0.4)`,
            } : {
              background: 'linear-gradient(to right, #facc15, #ca8a04)',
              borderColor: '#fde047',
              color: '#000',
            }}
          >
            <Crown className="w-3 h-3 mr-1" />
            PRESTIGE
          </Badge>
        )}

        {/* Bouton API */}
        {userId && serverData && userId === serverData.owner_id && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs px-2 py-0.5 h-6 bg-slate-800/80 hover:bg-slate-700/80 border-slate-600 text-blue-300 hover:text-blue-200"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAPIDialog(true); }}
            aria-label="API de vote et webhooks"
          >
            <Code className="w-3 h-3 mr-1" aria-hidden="true" />
            API
          </Button>
        )}

        {/* Admin Panel */}
        {serverData && (
          <AdminPanel
            server={{
              id: serverData.id,
              name: serverData.name,
              is_sponsored: serverData.is_sponsored,
              prestige_expires_at: serverData.prestige_expires_at,
              created_at: serverData.created_at,
              updated_at: serverData.updated_at,
            }}
          />
        )}
      </div>

      {/* Dialog API */}
      {userId && serverData && (
        <Dialog open={showAPIDialog} onOpenChange={setShowAPIDialog}>
          <DialogContent
            className="max-w-4xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <DialogHeader>
              <DialogTitle>API de Vote - {serverData.name}</DialogTitle>
            </DialogHeader>
            <VoteAPIManager serverId={serverData.id} serverName={serverData.name} apiToken={apiToken} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
